import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { extractNumericId } from '../../lib/userIdExtractor';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface RejectedPayment {
  id: string;
  amount: number;
  shares_to_purchase: number;
  network: string;
  transaction_hash: string | null;
  updated_at: string;
  rejection_reason: string | null;  // Admin's rejection reason from dedicated column
  transaction_notes?: string | null;
  approved_by_admin_id?: number | null;
}

interface RejectedPaymentsListProps {
  userId: number;
  onResubmit: (payment: RejectedPayment) => void;
}

export const RejectedPaymentsList: React.FC<RejectedPaymentsListProps> = ({
  userId,
  onResubmit
}) => {
  const [payments, setPayments] = useState<RejectedPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadRejectedPayments();
  }, [userId]);

  const loadRejectedPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate userId is numeric before querying
      const validUserId = extractNumericId(userId);
      if (!validUserId) {
        console.error('❌ Invalid userId for RejectedPaymentsList:', userId);
        setError('Invalid user ID. Please refresh the page or log out and log back in.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId: validUserId, dataTypes: ['rejected_payments'] })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      // Defensive: the dashboard-data endpoint returns HTTP 200 but sets
      // result.data[dataType] = { error: '...' } (a NON-array) when a per-dataType
      // query fails. `|| []` does not catch a truthy non-array, so guard with
      // Array.isArray to keep payments an array and never crash payments.map().
      const rejected = result.data?.rejected_payments;
      if (rejected && !Array.isArray(rejected)) {
        throw new Error((rejected as any).error || 'Failed to load rejected payments');
      }
      setPayments(Array.isArray(rejected) ? rejected : []);
    } catch (err: any) {
      console.error('Error loading rejected payments:', err);
      setError(err.message || 'Failed to load rejected payments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getRejectionReason = (payment: RejectedPayment): string => {
    // PRIORITY 1: Check the dedicated rejection_reason column (set by admin)
    if (payment.rejection_reason && payment.rejection_reason.trim()) {
      return payment.rejection_reason;
    }

    // PRIORITY 2: Try to extract from transaction_notes JSON (fallback for legacy data)
    if (payment.transaction_notes) {
      try {
        const notes = JSON.parse(payment.transaction_notes);
        if (notes.rejection_reason) return notes.rejection_reason;
        if (notes.admin_notes) return notes.admin_notes;
      } catch {
        // Not JSON, use as-is if it looks like a reason
        if (payment.transaction_notes.trim()) {
          return payment.transaction_notes;
        }
      }
    }
    return 'No reason provided';
  };

  const handleResubmit = (payment: RejectedPayment) => {
    const reason = getRejectionReason(payment);
    const confirmed = window.confirm(
      `You are about to create a NEW payment for $${payment.amount.toFixed(2)}.\n\n` +
      `Your previous payment was rejected because:\n"${reason}"\n\n` +
      `Make sure you understand the rejection reason before proceeding. Continue?`
    );
    if (confirmed) {
      onResubmit(payment);
    }
  };

  const handleDelete = async (payment: RejectedPayment) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete this rejected payment?\n\n` +
      `Amount: $${payment.amount.toFixed(2)}\n` +
      `Shares: ${payment.shares_to_purchase}\n` +
      `Network: ${payment.network}\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingId(payment.id);

      // Validate userId before delete operation
      const validUserId = extractNumericId(userId);
      if (!validUserId) {
        console.error('❌ Invalid userId for delete operation:', userId);
        alert('Invalid user ID. Please refresh the page or log out and log back in.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId: validUserId,
          operation: 'delete_rejected_payment',
          data: { paymentId: payment.id }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Refresh the list after successful deletion
      await loadRejectedPayments();
    } catch (err: any) {
      console.error('Error deleting rejected payment:', err);
      alert('Failed to delete payment: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '20px', border: '2px solid #ef4444', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ color: '#ef4444' }}>Loading rejected payments...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', marginBottom: '16px' }}>
        <p style={{ color: '#ef4444', margin: 0 }}>{error}</p>
        <button onClick={loadRejectedPayments} style={{ marginTop: '8px', padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return null; // Don't show section if no rejected payments
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ color: '#ef4444', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>❌</span> Rejected Payments ({payments.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {payments.map(payment => (
          <div key={payment.id} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <div style={{ color: '#ef4444', fontSize: '18px', fontWeight: 'bold' }}>${payment.amount.toFixed(2)}</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>{payment.shares_to_purchase} shares • {payment.network}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#ffffff', fontSize: '13px' }}>Rejected on</div>
                <div style={{ color: '#ef4444', fontSize: '13px' }}>{formatDate(payment.updated_at)}</div>
              </div>
            </div>
            {/* Prominent rejection reason */}
            <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <span style={{ color: '#ef4444', fontWeight: '600' }}>Rejection Reason:</span>
              </div>
              <p style={{ color: '#fca5a5', margin: 0, fontSize: '14px', lineHeight: '1.4' }}>{getRejectionReason(payment)}</p>
            </div>
            {payment.transaction_hash && (
              <div style={{ marginBottom: '12px', color: '#ffffff', fontSize: '13px' }}>
                Original Hash: <span style={{ fontFamily: 'monospace' }}>{payment.transaction_hash.slice(0, 20)}...</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleResubmit(payment)}
                disabled={deletingId === payment.id}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: deletingId === payment.id ? 'not-allowed' : 'pointer',
                  opacity: deletingId === payment.id ? 0.6 : 1
                }}
              >
                Resubmit Payment
              </button>
              <button
                onClick={() => handleDelete(payment)}
                disabled={deletingId === payment.id}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(107, 114, 128, 0.3)',
                  border: '1px solid rgba(107, 114, 128, 0.5)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontWeight: '600',
                  cursor: deletingId === payment.id ? 'not-allowed' : 'pointer',
                  opacity: deletingId === payment.id ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {deletingId === payment.id ? '⏳' : '🗑️'} Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RejectedPaymentsList;

